import React, { useEffect, useRef, useState } from "react";
import { getColumns } from "./columns";
import NotePopover from "./formatter/NotePopover";
import { dateFormatter } from "./formatter/dateFormatter";
import EmailFormatter from "./formatter/EmailFormatter";
import { linkMask } from "./formatter/linkMask";
import SelectFilter from "./menu/selectFilter";
import PinSelector from "./menu/PinSelector";
import SizeSelector from "./menu/SizeSelector";
import GroupingSelector from "./menu/GroupingSelector";
import ThemeSelector from "./menu/ThemeSelector";
import { Nav, Navbar } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { simpleResultProcessing } from "../../QueryBuilder/src/SimpleResultProcessing";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import "ag-grid-community/dist/styles/ag-theme-fresh.css";
import "ag-grid-community/dist/styles/ag-theme-blue.css";
import "../style.css";
import { useDispatch, useSelector } from "react-redux";
import { setApplications } from "../../../state/slices/application";
import NoteCellEditor from "./editor/NoteCellEditor";
import TextCellEditor from "./editor/TextCellEditor";
import SelectCellEditor from "./editor/SelectCellEditor";
import NumberCellEditor from "./editor/NumberCellEditor";
import LongTextCellEditor from "./editor/LongTextCellEditor";
import { useGetAllApplicationsQuery } from "../../../state/services/applications";

const DEBUG = false;

// This is the main component of Ag-Grid.
// It sets up the scene (so to speak) and returns <AgGridReact />
function Grid(props) {
  const { year, term, gridApi, setGridApi, gridColumnApi, setGridColumnApi } =
    props; // needed to pull up the state gridApi & gridColumnApi to share it with RankApplicationComponent
  const token = useSelector((state) => state.auth.token);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const applications = useSelector((state) => state.applications.records);
  const query = useSelector((state) => state.query);
  const dispatch = useDispatch();
  const [columnDefs] = useState(getColumns(userInfo.role));
  const [pagination] = useState(false);
  const [paginationSize] = useState(20);
  const [tableStyle, setTableStyle] = useState({
    height: Math.round(window.innerHeight * 0.7),
  });

  const [gridTheme, setGridTheme] = useState("ag-theme-balham");
  const preventRerender = useRef(false);

  useEffect(() => {
    DEBUG && console.log("useEffect for setting the theme is fired up!");
    switch (gridTheme) {
      case "ag-theme-balham":
        gridApi && gridApi.setHeaderHeight(27);
        break;
      case "ag-theme-alpine":
        gridApi && gridApi.setHeaderHeight(41);
        break;
      default:
        gridApi && gridApi.setHeaderHeight(25);
    }
  }, [gridApi, gridTheme]);

  const {
    isLoading,
    isError,
    data: dataSource,
    error,
  } = useGetAllApplicationsQuery({ year, term });

  useEffect(() => {
    DEBUG && console.log("useEffect for data source is fired up!");
    if (dataSource) {
      if (preventRerender.current === true) {
        preventRerender.current = false;
      } else {
        DEBUG &&
          console.log(
            "Going to run data source through the query builder filter!"
          );
        const newData = simpleResultProcessing(dataSource, query);
        DEBUG && console.log("Going to set applications in Redux store!");
        dispatch(setApplications(newData));
      }
    } else if (error) {
      console.log(error);
    }
  }, [dataSource, error, dispatch, query]);

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filterParams: {
      buttons: ["apply", "reset"],
      suppressAndOrCondition: true,
    },
    // rowDrag: (params) => {
    //   const displayedColumns = params.columnApi.getAllDisplayedColumns();
    //   return displayedColumns[0] === params.column;
    // },
    // checkboxSelection:  (params) => {
    //   const displayedColumns = params.columnApi.getAllDisplayedColumns();
    //   return displayedColumns[0] === params.column;
    // },
  };

  const handleQuickFilter = (event) => {
    gridApi.setQuickFilter(event.target.value);
  };

  const onEditComplete = (params) => {
    // Not doing anything here!
    // All the cell editors are hooked into redux store and they update the data as one edits them!
  };

  return (
    <div className={gridTheme} style={tableStyle}>
      <Navbar bg="light" variant="light" size="sm" expand="sm">
        {/*<Navbar.Toggle />*/}
        <Navbar.Collapse>
          <Nav className="me-auto" style={{ marginRight: "2rem" }}>
            <ThemeSelector gridApi={gridApi} setGridTheme={setGridTheme} />
            <SizeSelector
              gridColumnApi={gridColumnApi}
              setTableStyle={setTableStyle}
              initialHeight={tableStyle.height}
            />
            <GroupingSelector
              gridApi={gridApi}
              gridColumnApi={gridColumnApi}
              columnDefs={columnDefs}
            />
            <PinSelector gridApi={gridApi} />
          </Nav>
        </Navbar.Collapse>
        <input
          style={{ width: "100%" }}
          type="text"
          placeholder="Quick Filter"
          onChange={handleQuickFilter}
        />
      </Navbar>
      <AgGridReact
        frameworkComponents={{
          emailFormatter: EmailFormatter,
          notePopover: NotePopover,
          linkMask: linkMask,
          dateFormatter: dateFormatter,
          selectFilter: SelectFilter,
          numericCellEditor: NumberCellEditor,
          noteCellEditor: NoteCellEditor,
          textCellEditor: TextCellEditor,
          longTextCellEditor: LongTextCellEditor,
          selectCellEditor: SelectCellEditor,
        }}
        defaultColDef={defaultColDef}
        onGridReady={(params) => {
          setGridApi(params.api);
          setGridColumnApi(params.columnApi);
        }}
        rowSelection={"multiple"}
        rowDeselection={true}
        suppressRowClickSelection={true}
        columnDefs={columnDefs}
        rowData={isLoading || isError ? [] : !applications ? [] : applications}
        paginationPageSize={paginationSize}
        pagination={pagination}
        deltaColumnMode={true}
        // debug={true}
        rowDragManaged={true}
        animateRows={
          false
        } /* works nice with row drag but annoying with sorting! */
        enableMultiRowDragging={true}
        // rowClassRules={{
        //   "ag-red": (params) => params.data.status === "Withdrawn",
        // }}
        // domLayout="autoHeight"
        undoRedoCellEditing={true}
        undoRedoCellEditingLimit={5}
        onCellValueChanged={onEditComplete}
        // To work with Redux
        immutableData={true} // need this to integrate with Redux
        getRowNodeId={(data) => data.row} // must implement this for immutableData
      />
    </div>
  );
}

export default Grid;
